/**
 * Agent Definitions 훅 — 프리셋 에이전트 조회 전용
 *
 * - useAgentDefinitions(): 프리셋 목록 (+ DB의 공유 에이전트 merge)
 * - useAgentDefinition(id): 단일 에이전트 정의
 *
 * 커스텀 에이전트 생성/수정/삭제 기능은 제거됨.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { agentKeys } from "@/api/hooks/query-keys";
import { PRESET_AGENTS, getPresetAgent } from "@modootoday/core-agent/agents";
import type { AgentDefinition, AgentTier } from "@modootoday/core-agent/types";

/* ══════════════════════════════════════════════════════════════════════════
   DB 행 → AgentDefinition 변환
   ══════════════════════════════════════════════════════════════════════════ */

interface AgentDefinitionRow {
  id: string;
  user_id: string | null;
  name: string;
  description: string | null;
  icon: string;
  tools: string[];
  starters: string[];
  is_shared: boolean;
  required_tier: string;
  created_at: string;
  updated_at: string;
  // system_prompt: 뷰에서 제외됨 (SECURITY DEFINER 함수로만 접근)
}

function rowToDefinition(row: AgentDefinitionRow): AgentDefinition {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? undefined,
    icon: row.icon,
    systemPrompt: "", // system_prompt는 Edge Function 내부에서만 사용
    tools: row.tools ?? [],
    starters: row.starters ?? [],
    requiredTier: row.required_tier as AgentTier,
    isShared: row.is_shared,
    userId: row.user_id ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   useAgentDefinitions — 프리셋 + 커스텀 병합
   ══════════════════════════════════════════════════════════════════════════ */

export interface UseAgentDefinitionsOptions {
  /** 특정 tier 이하 에이전트만 필터 */
  tier?: AgentTier;
  /** 커스텀 에이전트만 */
  customOnly?: boolean;
}

export function useAgentDefinitions(opts: UseAgentDefinitionsOptions = {}) {
  const { tier, customOnly } = opts;

  return useQuery({
    queryKey: agentKeys.definitions(),
    queryFn: async (): Promise<AgentDefinition[]> => {
      // agent_definitions_shared 뷰에서 공유/프리셋 + 내 커스텀 에이전트 조회
      const { data, error } = await supabase
        .from("agent_definitions_shared")
        .select(
          "id,user_id,name,description,icon,tools,starters,is_shared,required_tier,created_at,updated_at",
        )
        .order("created_at", { ascending: true });

      if (error) {
        // DB 조회 실패 시 프리셋만 반환 (graceful degradation)
        console.warn(
          "[use-agent-definitions] DB fetch failed, using presets only:",
          error,
        );
        return filterDefinitions(
          PRESET_AGENTS.map((p) => ({ ...p, systemPrompt: "" })),
          { tier, customOnly },
        );
      }

      const dbDefs = ((data as AgentDefinitionRow[]) ?? []).map(
        rowToDefinition,
      );

      // 프리셋 ID 세트
      const presetIds = new Set(PRESET_AGENTS.map((p) => p.id));

      // DB에 없는 프리셋 추가 (PRESET_AGENTS가 단일 진실원)
      const dbIds = new Set(dbDefs.map((d) => d.id));
      const missingPresets = PRESET_AGENTS.filter((p) => !dbIds.has(p.id)).map(
        (p) => ({ ...p, systemPrompt: "" }),
      );

      // DB에서 가져온 프리셋은 PRESET_AGENTS의 메타데이터로 보강
      const merged: AgentDefinition[] = [
        ...dbDefs.map((d) => {
          if (presetIds.has(d.id)) {
            const preset = getPresetAgent(d.id);
            return preset
              ? {
                  ...d,
                  name: preset.name,
                  description: preset.description,
                  icon: preset.icon,
                }
              : d;
          }
          return d;
        }),
        ...missingPresets,
      ];

      return filterDefinitions(merged, { tier, customOnly });
    },
    staleTime: 5 * 60 * 1000, // 5분
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   useAgentDefinition — 단일 에이전트
   ══════════════════════════════════════════════════════════════════════════ */

export function useAgentDefinition(agentId: string | null) {
  return useQuery({
    queryKey: agentKeys.definition(agentId ?? ""),
    queryFn: async (): Promise<AgentDefinition | null> => {
      if (!agentId) return null;

      // 프리셋 우선
      const preset = getPresetAgent(agentId);
      if (preset) return { ...preset, systemPrompt: "" };

      // DB 조회
      const { data, error } = await supabase
        .from("agent_definitions_shared")
        .select(
          "id,user_id,name,description,icon,tools,starters,is_shared,required_tier,created_at,updated_at",
        )
        .eq("id", agentId)
        .single();

      if (error || !data) return null;
      return rowToDefinition(data as AgentDefinitionRow);
    },
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   헬퍼
   ══════════════════════════════════════════════════════════════════════════ */

const TIER_ORDER: AgentTier[] = ["free", "starter", "pro", "enterprise"];

function tierIndex(tier: AgentTier): number {
  return TIER_ORDER.indexOf(tier);
}

function filterDefinitions(
  defs: AgentDefinition[],
  opts: { tier?: AgentTier; customOnly?: boolean },
): AgentDefinition[] {
  const { tier, customOnly } = opts;
  let result = defs;

  if (tier !== undefined) {
    const maxIdx = tierIndex(tier);
    result = result.filter((d) => tierIndex(d.requiredTier) <= maxIdx);
  }

  if (customOnly) {
    const presetIds = new Set(PRESET_AGENTS.map((p) => p.id));
    result = result.filter((d) => !presetIds.has(d.id));
  }

  return result;
}
